import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Calendar, Clock, User } from "lucide-react";
import { cn } from "@/lib/utils";
import PortableTextRenderer from "./portable-text-renderer";
import ShareButtons from "./ShareButtons";
import { SummarizeWith, AskAIExplain } from "@/components/ask-ai-to-explain";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildBlogPrompt } from "@/data/explain-with-ai";
import type { SanityArticle, SanityArticleSummary, SanityCategoryWithCount, SanityRecentPost } from "@/lib/sanity.types";

type BlogPostProps = {
  article: SanityArticle;
  categories?: SanityCategoryWithCount[];
  recentPosts?: SanityRecentPost[];
  popularTags?: string[];
  relatedPosts?: SanityArticleSummary[];
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function calculateReadingTime(body: Record<string, any>[]): number {
  if (!body) return 3;
  const text = body
    .filter((block) => block._type === "block")
    .map((block) => (block.children ?? []).map((c: any) => c?.text ?? "").join(""))
    .join(" ");
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

function extractHeadings(body: Record<string, any>[]) {
  if (!body) return [];
  const headings: { text: string; level: number; slug: string }[] = [];

  for (const block of body) {
    if (!block || typeof block !== "object" || block._type !== "block") continue;
    if (block.style !== "h2" && block.style !== "h3") continue;

    const text = (block.children ?? [])
      .map((child: any) => child?.text ?? "")
      .join("")
      .trim();
    if (!text) continue;

    const level = block.style === "h2" ? 2 : 3;
    const slug = text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/(^-|-$)+/g, "");
    headings.push({ text, level, slug });
  }
  return headings;
}

export default function BlogPost({ article, categories, recentPosts, popularTags, relatedPosts }: BlogPostProps) {
  const [activeId, setActiveId] = useState<string>("");
  const [tocExpanded, setTocExpanded] = useState(false);
  const readingTime = calculateReadingTime(article.body);
  const headings = extractHeadings(article.body);
  const authorName = article.author?.name || "Zapify Team";
  const authorSlug = article.author?.slug || authorName.toLowerCase().replace(/\s+/g, "-");
  const articleUrl = `https://zapify.pro/article/${article.slug}`;

  useEffect(() => {
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);
        if (visible?.target?.id) setActiveId(visible.target.id);
      },
      { rootMargin: "-100px 0px -80% 0px" }
    );

    headings.forEach((item) => {
      const el = document.getElementById(item.slug);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings]);

  return (
    <>
    <article className="relative z-10 bg-background max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-20">
      {/* Breadcrumbs */}
      <nav className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary transition-colors">Home</Link>
        <span>/</span>
        <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
        <span>/</span>
        <span className="text-foreground truncate max-w-[250px] sm:max-w-[400px]">{article.title}</span>
      </nav>

      {/* Two Column Layout */}
      <div className="grid gap-12 lg:grid-cols-[1fr_300px]">
        {/* Main Content */}
        <div className="min-w-0">
          {/* Header */}
          <header className="mb-8">
            {/* Category */}
            {article.category && (
              <div className="mb-4">
                <Link
                  to={`/blog/category/${article.category.slug}`}
                  className="rounded-full px-3 py-1 text-xs font-medium bg-primary-gradient text-primary-foreground inline-block transition-opacity hover:opacity-90"
                >
                  {article.category.name}
                </Link>
              </div>
            )}

            {/* Title */}
            <h1 className="text-[33.5px] lg:text-[45px] font-extrabold leading-[1.1] tracking-[-1px] text-foreground mb-4">
              {article.title}
            </h1>

            {/* Excerpt */}
            {article.description && (
              <p className="text-[18px] text-muted-foreground leading-relaxed italic mb-6">
                {article.description}
              </p>
            )}

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-[14px] text-muted-foreground">
              {article.author && (
                <Link
                  to={`/blog/author/${authorSlug}`}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  {article.author.avatarUrl ? (
                    <img
                      src={article.author.avatarUrl}
                      alt={authorName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  <span className="font-medium">{authorName}</span>
                </Link>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{readingTime} min read</span>
              </span>
            </div>

            {/* Share Buttons */}
            <div className="mt-6">
              <ShareButtons title={article.title} url={articleUrl} />
            </div>

            {/* Summarize with AI */}
            <div className="mt-4">
              <SummarizeWith
                prompt={buildBlogPrompt(article.title, articleUrl)}
              />
            </div>
          </header>

          {/* Featured Image */}
          {article.cover && (
            <div className="relative mb-12 aspect-video overflow-hidden rounded-lg bg-secondary">
              <img
                src={article.cover.url}
                alt={article.cover.alternativeText || article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg dark:prose-invert max-w-none">
            {article.body && article.body.length > 0 ? (
              <PortableTextRenderer body={article.body} />
            ) : null}
          </div>

          {/* Tags — clickable, link to blog filtered by search */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-muted-foreground">Tags:</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to="/blog"
                  search={{ page: 1, q: tag }}
                  className="rounded-full border border-border bg-background px-3 py-1 text-sm capitalize hover:bg-secondary hover:border-ring/40 transition-colors"
                >
                  {tag.replace(/-/g, " ")}
                </Link>
              ))}
            </div>
          )}

          {/* Author Bio — using real data from Sanity */}
          {article.author && (
            <div className="mt-12 rounded-lg border border-border bg-card p-6">
              <div className="flex items-start gap-4">
                {article.author.avatarUrl ? (
                  <img
                    src={article.author.avatarUrl}
                    alt={authorName}
                    className="w-16 h-16 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary-gradient flex items-center justify-center text-primary-foreground font-bold text-xl shrink-0">
                    {authorName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="mb-1 text-lg font-bold text-foreground">{authorName}</h3>
                  {article.author.bio ? (
                    <p className="text-sm text-muted-foreground">{article.author.bio}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Author at Zapify, writing about Instagram automation and social media growth.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FAQs */}
          {article.faqs && article.faqs.length > 0 && (
            <section className="mt-12">
              <h2 className="mb-6 text-2xl md:text-3xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {article.faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="border-border"
                  >
                    <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </div>
        <aside className="hidden lg:block self-start sticky top-28 space-y-8">
            {/* Table of Contents */}
            {headings.length > 0 && (
              <nav className="rounded-lg border border-border bg-card p-6">
                <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Table of Contents
                </h3>
                <ul className="space-y-2 text-sm">
                  {(tocExpanded ? headings : headings.slice(0, 5)).map((heading) => (
                    <li
                      key={heading.slug}
                      style={{ paddingLeft: `${(heading.level - 2) * 12}px` }}
                    >
                      <a
                        href={`#${heading.slug}`}
                        className={cn(
                          "block py-1 transition-colors hover:text-primary",
                          activeId === heading.slug
                            ? "font-medium text-primary"
                            : "text-muted-foreground"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(heading.slug)?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }}
                      >
                        {heading.text}
                      </a>
                    </li>
                  ))}
                </ul>
                {headings.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setTocExpanded(!tocExpanded)}
                    className="mt-3 text-sm font-medium text-primary hover:underline"
                  >
                    {tocExpanded ? "Show less" : `View all (${headings.length})`}
                  </button>
                )}
              </nav>
            )}

            {/* More by Author */}
            {recentPosts && recentPosts.length > 0 && (
              <div className="rounded-lg border border-border bg-card p-6">
                <h3 className="mb-4 text-lg font-bold text-foreground">More by {authorName}</h3>
                <div className="space-y-4">
                  {recentPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/article/${post.slug}`}
                      className="group flex gap-3"
                    >
                      {post.cover?.url && (
                        <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded bg-secondary">
                          <img
                            src={post.cover.url}
                            alt={post.cover.alternativeText || post.title}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors text-foreground">
                          {post.title}
                        </h4>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatShortDate(post.publishedAt)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to={`/blog/author/${authorSlug}`}
                  className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  View all posts by {authorName} →
                </Link>
              </div>
            )}

            {/* Categories */}
            {categories && categories.length > 0 && (
              <div className="rounded-lg border border-border bg-card p-6">
                <h3 className="mb-4 text-lg font-bold text-foreground">Categories</h3>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/blog/category/${cat.slug}`}
                      className="flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
                    >
                      <span className="capitalize">{cat.name}</span>
                      <span className="text-xs text-muted-foreground">{cat.count}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Popular Tags */}
            {popularTags && popularTags.length > 0 && (
              <div className="rounded-lg border border-border bg-card p-6">
                <h3 className="mb-4 text-lg font-bold text-foreground">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map((tag) => (
                    <Link
                      key={tag}
                      to="/blog"
                      search={{ page: 1, q: tag }}
                      className="rounded-full border border-border bg-background px-3 py-1 text-xs capitalize hover:bg-secondary hover:border-ring/40 transition-colors"
                    >
                      {tag.replace(/-/g, " ")}
                    </Link>
                  ))}
                </div>
              </div>
            )}
        </aside>
      </div>

      {/* Related Posts */}
      {relatedPosts && relatedPosts.length > 0 && (
        <section className="mt-16 pt-12 border-t border-border">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Related Posts</h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {relatedPosts.map((post) => (
              <Link
                key={post.id}
                to={`/article/${post.slug}`}
                className="group rounded-xl border border-border bg-card overflow-hidden transition-all hover:shadow-soft hover:border-ring/30"
              >
                {post.cover?.url && (
                  <div className="aspect-video overflow-hidden bg-secondary">
                    <img
                      src={post.cover.url}
                      alt={post.cover.alternativeText || post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-5">
                  {post.category && (
                    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-medium text-primary bg-primary/10 mb-2">
                      {post.category.name}
                    </span>
                  )}
                  <h3 className="text-base font-bold text-foreground leading-snug mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{post.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
    <AskAIExplain
      context="blog"
      meta={{ articleTitle: article.title, pageUrl: articleUrl }}
      isWidget
    />
    </>
  );
}
